import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BreadcrumbNav from '@/components/feature/BreadcrumbNav';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { videos, getVideosByCategory } from '@/mocks/videos';

const categoryNames: Record<string, string> = {
  entertainment: '娱乐综艺', education: '教育学习', technology: '科技数码',
  lifestyle: '生活美食', sports: '体育竞技', music: '音乐舞蹈',
  gaming: '游戏动漫', documentary: '纪录纪实', fashion: '时尚美妆', kids: '少儿乐园',
};

const formatViews = (n: number) => {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万次观看`;
  return `${n}次观看`;
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  const filteredVideos = useMemo(() => {
    let list = activeSlug === 'all' ? videos : getVideosByCategory(activeSlug);
    if (sortBy === 'popular') {
      return [...list].sort((a, b) => b.views - a.views);
    }
    return [...list].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [activeSlug, sortBy]);

  const activeCategory = activeSlug === 'all' ? null : categories.find((c) => c.slug === activeSlug);

  const pageTitle = activeCategory
    ? `${activeCategory.name} - 91免费视频`
    : '全部分类 - 91免费视频';
  const pageDesc = activeCategory
    ? `浏览${activeCategory.name}分类下的全部免费高清视频，海量精选内容每日更新。`
    : '浏览91免费视频全部分类的免费高清视频，涵盖娱乐、教育、科技等多种类型。';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`91免费视频,${activeCategory ? activeCategory.name + ',' : ''}免费视频在线观看,高清免费视频,视频分类`}
        canonicalUrl={`${window.location.origin}${__BASE_PATH__}/category/${activeSlug}`}
        schemaData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: pageTitle,
          description: pageDesc,
          url: `${window.location.origin}${__BASE_PATH__}/category/${activeSlug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '91免费视频',
            url: `${window.location.origin}${__BASE_PATH__}/`,
          },
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-14 md:pt-16">
          <div className="max-w-6xl mx-auto">
            <BreadcrumbNav
              items={[
                { label: '首页', href: '/' },
                ...(activeCategory ? [{ label: '视频分类' }] : []),
                { label: activeCategory ? activeCategory.name : '全部分类' },
              ]}
            />
          </div>

          {/* Category Tabs */}
          <div className="w-full px-4 md:px-6 py-6 bg-background-100 border-y border-background-200/60">
            <div className="max-w-6xl mx-auto">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                {activeCategory ? activeCategory.name : '全部分类'}
              </h1>
              <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-wrap">
                <Link
                  to="/category/entertainment"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSlug('all');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeSlug === 'all'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200/60 border border-background-200/60'
                  }`}
                >
                  全部
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveSlug(cat.slug);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-200/60 border border-background-200/60'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Sort & Results */}
          <div className="w-full px-4 md:px-6 py-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-foreground-500">
                  共 <strong className="text-foreground-900">{filteredVideos.length}</strong> 个视频
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSortBy('latest')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === 'latest'
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200/60'
                    }`}
                  >
                    最新发布
                  </button>
                  <button
                    onClick={() => setSortBy('popular')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === 'popular'
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200/60'
                    }`}
                  >
                    最多观看
                  </button>
                </div>
              </div>

              {filteredVideos.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-video-off-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">暂无视频内容</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {filteredVideos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/article/${video.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                    >
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-white text-xs rounded-md font-medium">
                          {video.duration}
                        </div>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                          <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-play-fill text-primary-500 text-lg ml-0.5"></i>
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 mb-2 leading-snug group-hover:text-primary-600 transition-colors">
                          {video.title}
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {formatViews(video.views)}
                          </span>
                          <span>{video.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}